import {
  CircularProgress,
  Container,
  Divider,
  Tooltip,
} from "@material-ui/core";
import { Grid, styled } from "@mui/material";
import axios from "axios";
import {
  ImportDocumentModalCWR,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SkeletonComponent,
  StatusChip,
} from "components";
import { InnoTableV2 } from "inno-ui";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors } from "utils";
import ExcelLogo from "assets/img/excel-logo.png";
import Swal from "sweetalert2";

function CWR({ userLogin }) {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const roleType = userLogin.role.type;
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");

  const [loadingPage, setLoadingPage] = useState(false);
  const [modalDocument, setModalDocument] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [optionVersion, setOptionVersion] = useState([]);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const [loadingButton, setLoadingButton] = useState(false);
  const [societyFormat, setSocietyFormat] = useState("asaba");

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const params = {
        ...queryParams,
      };
      const res = await axios.get(`${hardBaseUrl}/cwr`, {
        headers,
        params,
      });

      const { data, meta } = res?.data;
      setDataTable(data);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const getOptionVersion = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/cwr/available-version`, {
        headers,
        params: {},
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.value,
        label: item?.label,
      }));
      setOptionVersion(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const onClickDownload = download_url =>
    Swal.fire({
      icon: "success",
      title: "Data can only be downloaded for 7 days",
      confirmButtonText: "Close",
      didRender: () => window.open(download_url),
    });

  const handleExportTemplate = async () => {
    try {
      setLoadingButton(true);

      await axios.post(
        `${hardBaseUrl}/cwr/export-excel`,
        { type: "excel" },
        {
          headers,
        }
      );
      ModalSuccess(
        "You can check the progress of the download process in Download Monitoring menu",
        "Data is being processed"
      ).then(() => {
        history.push("/admin/download-monitoring");
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
    }
  };
  const handleUploadDocument = async (documentFiles, payload) => {
    const formData = new FormData();
    formData.append("file", documentFiles[0]);
    formData.append("version", payload.version);
    try {
      setLoadingButton(true);
      await axios.post(`${hardBaseUrl}/cwr/import-excel`, formData, {
        headers,
      });
      ModalSuccess(
        "You can check the progress of the upload process in the CWR Engine menu",
        "Data is being processed"
      ).then(() => {
        getDataTable();
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
      setModalDocument(false);
    }
  };

  const handleErrorDetail = id => {
    history.push(`/admin/cwr-engine/${id}`);
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);
  useEffect(() => {
    getOptionVersion();
  }, []);

  const statusCondition = {
    Finish: "success",
    Error: "danger",
    Started: "warning",
    Initialize: "warning",
    Running: "warning",
  };

  return (
    <Page className={classes.root} title="CWR ENGINE">
      <Container maxWidth={false}>
        <HeaderTitle title="CWR ENGINE" breadcrumbData={breadcrumbData} />
        <CustomDivider />
        <Grid container justifyContent="space-between" mb="16px" mt="16px">
          <Grid item>
            <Grid container columnSpacing={1}>
              <Grid item>
                <SearchTextInput
                  placeholder="Search"
                  value={queryParams?.search}
                  onChange={e =>
                    handleChangeQueryParams(e?.target?.value, "search")
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Tooltip title="Download Template">
                <Grid item>
                  <PrimaryButton
                    onClick={handleExportTemplate}
                    disabled={loadingButton}
                    label={
                      loadingButton ? (
                        <CircularProgress size="24px" color="inherit" />
                      ) : (
                        "Download Template"
                      )
                    }
                    height="42px"
                  />
                </Grid>
              </Tooltip>
              <Tooltip title="Import Document">
                <Grid item>
                  <PrimaryButton
                    onClick={() => setModalDocument(true)}
                    label={<img src={ExcelLogo} alt="logo" />}
                    height="42px"
                    color="#34774C"
                  />
                </Grid>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        {loadingPage ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <InnoTableV2
            isLoading={false}
            columns={columnTable(
              handleErrorDetail,
              onClickDownload,
              statusCondition
            )}
            items={dataTable || []}
            page={queryParams?.page}
            rowsPerPage={queryParams?.size}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            onRequestSort={sortBy => {
              handleChangeQueryParams(sortBy, "sort");
            }}
          />
        )}
      </Container>
      {modalDocument && (
        <ImportDocumentModalCWR
          open={modalDocument}
          onClose={() => setModalDocument(false)}
          onSubmit={handleUploadDocument}
          fileType=".xlsx, .xls"
          loadingButton={loadingButton}
          optionVersion={optionVersion}
          downloadFile
          roleType={roleType}
          onChangeFormat={
            societyFormat ? value => setSocietyFormat(value) : null
          }
        />
      )}
    </Page>
  );
}

const CustomDivider = styled(Divider)(() => ({
  marginBottom: 16,
  borderTop: "1px solid #e1e1e1",
}));

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "CWR ENGINE",
    active: true,
  },
];

const columnTable = (handleErrorDetail, onClickDownload, statusCondition) => [
  {
    name: "file_name",
    title: "File Name",
    renderText: item => item || "-",
  },
  {
    name: "version",
    title: "Version",
    renderText: item => item || "-",
  },
  {
    name: "created_by",
    title: "Create By",
  },
  {
    name: "created_at",
    title: "Create On",
    renderText: date => formatDate(date),
  },
  {
    name: "status",
    title: "Status",
    renderText: status => (
      <StatusChip label={status} type={statusCondition[status]} />
    ),
  },
  {
    name: "all",
    title: "Download",
    width: 180,
    renderText: item => {
      const { status, download_url } = item;
      const isError = status === "Error";
      return (
        <Grid container direction="column">
          <Grid item>
            {status === "in progress" ? (
              "-"
            ) : (
              <PrimaryButton
                label={isError ? "Error Detail" : "Download"}
                width={120}
                onClick={() =>
                  isError
                    ? handleErrorDetail(item.id)
                    : onClickDownload(download_url)
                }
              />
            )}
          </Grid>
        </Grid>
      );
    },
  },
];
export default CWR;
